import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AIModel, Job } from '@volt/shared';
import { useI18n } from '../i18n/context.js';
import { useSession } from '../state/session.js';
import { useUI } from '../state/ui.js';
import { useJobs } from '../hooks/useJobs.js';
import { useGeneratedLibrary } from '../hooks/useGeneratedLibrary.js';
import { Icon } from './Icon.js';
import { LibraryPickerModal } from './LibraryPickerModal.js';
import { describeJobError } from '../lib/jobsPolling.js';

const AVATAR_PRESETS = ['ugc-female-01', 'ugc-male-01', 'host-neutral-01'] as const;
const VIDEO2_MODES = [
  'ugc',
  'ugc_how_to',
  'ugc_unboxing',
  'product_showcase',
  'product_review',
] as const;
const AD_FORMATS = ['none', 'dtc-ads', 'native-ads'] as const;

function parseIdList(value: string): string[] {
  return value
    .split(/[\n,]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

async function filesToDataUrls(files: ArrayLike<File> | null): Promise<string[]> {
  if (!files || files.length === 0) return [];
  return Promise.all(
    Array.from(files).map(
      (file) =>
        new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
          reader.onerror = () => reject(new Error('Failed to read file'));
          reader.readAsDataURL(file);
        }),
    ),
  );
}

const PRODUCT_IMAGE_KINDS = ['image'] as const;
const MAX_PRODUCT_IMAGES = 10;
/** Product photos travel inline (base64) in the job input, so each one is capped after encoding. */
const MAX_PRODUCT_IMAGE_ENCODED_BYTES = 2 * 1024 * 1024;

export function Video2JobScreen() {
  const { t } = useI18n();
  const { data } = useSession();
  const { toast } = useUI();
  const { jobs, loading, generating, create } = useJobs(true);

  const models = useMemo(
    () => (data?.models ?? []).filter((model) => model.section === 'video2' && model.isActive),
    [data?.models],
  );

  const [selectedModelId, setSelectedModelId] = useState('');
  const [prompt, setPrompt] = useState('');
  const [productSource, setProductSource] = useState<'url' | 'images' | 'existing'>('url');
  const [productUrl, setProductUrl] = useState('');
  const [productIdsInput, setProductIdsInput] = useState('');
  const [productTitle, setProductTitle] = useState('');
  const [productImages, setProductImages] = useState<string[]>([]);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const library = useGeneratedLibrary(PRODUCT_IMAGE_KINDS, { enabled: productSource === 'images' });
  const [avatarType, setAvatarType] = useState<'none' | 'preset' | 'custom'>('none');
  const [avatarPresetId, setAvatarPresetId] = useState<string>(AVATAR_PRESETS[0]);
  const [customAvatarIdsInput, setCustomAvatarIdsInput] = useState('');
  const [adApproach, setAdApproach] = useState<'reference' | 'blocks'>('blocks');
  const [adReferenceId, setAdReferenceId] = useState('');
  const [hookId, setHookId] = useState('');
  const [settingId, setSettingId] = useState('');
  const [mode, setMode] = useState<(typeof VIDEO2_MODES)[number]>('ugc');
  const [adFormat, setAdFormat] = useState<(typeof AD_FORMATS)[number]>('none');
  const [formatId, setFormatId] = useState('');
  const [duration, setDuration] = useState('15');
  const [resolution, setResolution] = useState('720p');
  const [aspectRatio, setAspectRatio] = useState('9:16');
  const [generateAudio, setGenerateAudio] = useState(true);

  useEffect(() => {
    if (!selectedModelId && models[0]) setSelectedModelId(models[0].id);
  }, [models, selectedModelId]);

  const selectedModel = useMemo<AIModel | undefined>(
    () => models.find((model) => model.id === selectedModelId) ?? models[0],
    [models, selectedModelId],
  );

  const sectionJobs = useMemo(
    () => jobs.filter((job) => ((job.input as { section?: string }).section ?? '') === 'video2'),
    [jobs],
  );

  /** Keeps at most `MAX_PRODUCT_IMAGES` photos of at most 2 MB each; warns when something was dropped. */
  const limitProductImages = useCallback(
    (existing: string[], added: string[]): string[] => {
      const small = added.filter((value) => value.length <= MAX_PRODUCT_IMAGE_ENCODED_BYTES);
      const next = [...existing, ...small].slice(0, MAX_PRODUCT_IMAGES);
      if (next.length < existing.length + added.length) {
        toast(t('video2.errorProductImagesLimit', { max: MAX_PRODUCT_IMAGES, size: '2 MB' }), 'error');
      }
      return next;
    },
    [t, toast],
  );

  const handleProductImagesChange = useCallback(
    async (files: FileList | null) => {
      const values = (await filesToDataUrls(files)).filter(Boolean);
      setProductImages(limitProductImages([], values));
    },
    [limitProductImages],
  );

  // Picked from the user's earlier generations: added to what is already chosen.
  const handleLibraryImages = useCallback(
    async (files: File[]) => {
      const values = (await filesToDataUrls(files)).filter(Boolean);
      setProductImages((prev) => limitProductImages(prev, values));
    },
    [limitProductImages],
  );

  const avatars = useMemo(() => {
    if (avatarType === 'preset' && avatarPresetId)
      return [{ id: avatarPresetId, type: 'preset' as const }];
    if (avatarType === 'custom') {
      return parseIdList(customAvatarIdsInput).map((id) => ({ id, type: 'custom' as const }));
    }
    return [];
  }, [avatarPresetId, avatarType, customAvatarIdsInput]);

  const productIds = useMemo(() => parseIdList(productIdsInput), [productIdsInput]);

  const canGenerate = Boolean(selectedModel) && Boolean(prompt.trim());

  const handleGenerate = useCallback(async () => {
    if (!selectedModel) return;
    const trimmedPrompt = prompt.trim();
    if (!trimmedPrompt) return;
    if (productSource === 'url' && !productUrl.trim()) {
      toast(t('video2.errorProductRequired'), 'error');
      return;
    }
    if (productSource === 'existing' && productIds.length === 0) {
      toast(t('video2.errorProductRequired'), 'error');
      return;
    }
    if (productSource === 'images' && (!productTitle.trim() || productImages.length === 0)) {
      toast(t('video2.errorProductRequired'), 'error');
      return;
    }
    if (adFormat === 'dtc-ads' && !formatId.trim()) {
      toast(t('video2.errorFormatRequired'), 'error');
      return;
    }
    if (adApproach === 'reference' && !adReferenceId.trim()) {
      toast(t('video2.errorAdApproachRequired'), 'error');
      return;
    }
    if (adApproach === 'blocks' && !hookId.trim() && !settingId.trim()) {
      toast(t('video2.errorAdApproachRequired'), 'error');
      return;
    }

    const options: Record<string, unknown> = {
      section: 'video2',
      avatars,
      mode,
      duration: Number(duration),
      resolution,
      aspect_ratio: aspectRatio,
      ad_format: adFormat === 'none' ? undefined : adFormat,
      format_id: adFormat === 'dtc-ads' ? formatId.trim() : undefined,
      generate_audio: generateAudio,
      click_to_ad_url: productSource === 'url' ? productUrl.trim() || undefined : undefined,
      product_title: productSource === 'images' ? productTitle.trim() || undefined : undefined,
      product_images: productSource === 'images' ? productImages : undefined,
    };
    if (productSource === 'existing' && productIds.length > 0) {
      options.product_ids = productIds;
    }

    if (adApproach === 'reference') {
      options.ad_reference_id = adReferenceId.trim() || undefined;
    } else {
      options.hook_id = hookId.trim() || undefined;
      options.setting_id = settingId.trim() || undefined;
    }

    try {
      await create({
        modelId: selectedModel.id,
        prompt: trimmedPrompt,
        options,
      });
      setPrompt('');
      setProductSource('url');
      setProductUrl('');
      setProductIdsInput('');
      setProductTitle('');
      setProductImages([]);
      setAvatarType('none');
      setAvatarPresetId(AVATAR_PRESETS[0]);
      setCustomAvatarIdsInput('');
      setAdApproach('blocks');
      setAdReferenceId('');
      setHookId('');
      setSettingId('');
      setMode('ugc');
      setAdFormat('none');
      setFormatId('');
      setDuration('15');
      setResolution('720p');
      setAspectRatio('9:16');
      setGenerateAudio(true);
    } catch (error: unknown) {
      toast(describeJobError(error, t), 'error');
    }
  }, [
    adApproach,
    adFormat,
    adReferenceId,
    aspectRatio,
    avatars,
    create,
    duration,
    formatId,
    generateAudio,
    hookId,
    mode,
    productIds,
    productSource,
    productImages,
    productTitle,
    productUrl,
    prompt,
    resolution,
    selectedModel,
    settingId,
    t,
    toast,
  ]);

  return (
    <section className="media-screen">
      <div className="job-composer">
        <h2 className="section-title">{t('section.video2')}</h2>

        <label className="field-label" htmlFor="video2-model">
          {t('composer.model')}
        </label>
        <select
          id="video2-model"
          className="field"
          value={selectedModel?.id ?? ''}
          onChange={(event) => setSelectedModelId(event.target.value)}
        >
          {models.map((model) => (
            <option key={model.id} value={model.id}>
              {model.displayName}
            </option>
          ))}
        </select>

        <label className="field-label" htmlFor="video2-prompt">
          {t('video2.prompt')}
        </label>
        <textarea
          id="video2-prompt"
          className="composer-input"
          value={prompt}
          onChange={(event) => setPrompt(event.target.value)}
          rows={4}
        />

        <label className="field-label" htmlFor="video2-product-source">
          {t('video2.productSource')}
        </label>
        <select
          id="video2-product-source"
          className="field"
          value={productSource}
          onChange={(event) =>
            setProductSource(event.target.value as 'url' | 'images' | 'existing')
          }
        >
          <option value="url">{t('video2.productFromUrl')}</option>
          <option value="images">{t('video2.productFromImages')}</option>
          <option value="existing">{t('video2.productExisting')}</option>
        </select>

        {productSource === 'url' && (
          <input
            className="field"
            value={productUrl}
            onChange={(event) => setProductUrl(event.target.value)}
            placeholder={t('video2.productUrl')}
          />
        )}
        {productSource === 'existing' && (
          <textarea
            className="composer-input"
            rows={2}
            value={productIdsInput}
            onChange={(event) => setProductIdsInput(event.target.value)}
            placeholder={t('video2.productIds')}
          />
        )}
        {productSource === 'images' && (
          <>
            <input
              className="field"
              value={productTitle}
              onChange={(event) => setProductTitle(event.target.value)}
              placeholder={t('video2.productTitle')}
            />
            <input
              className="field"
              type="file"
              accept="image/*"
              multiple
              onChange={(event) => void handleProductImagesChange(event.target.files)}
            />
            {library.hasItems && (
              <button
                type="button"
                className="btn btn-ghost lib-trigger"
                onClick={() => setLibraryOpen(true)}
              >
                <Icon name="folder" size={16} />
                {t('library.fromLibrary')}
              </button>
            )}
            <LibraryPickerModal
              open={libraryOpen}
              onClose={() => setLibraryOpen(false)}
              library={library}
              maxSelect={Math.max(1, MAX_PRODUCT_IMAGES - productImages.length)}
              onPick={handleLibraryImages}
            />
          </>
        )}

        <label className="field-label" htmlFor="video2-avatar-type">
          {t('video2.avatarType')}
        </label>
        <select
          id="video2-avatar-type"
          className="field"
          value={avatarType}
          onChange={(event) => setAvatarType(event.target.value as 'none' | 'preset' | 'custom')}
        >
          <option value="none">{t('video2.avatarNone')}</option>
          <option value="preset">{t('video2.avatarPreset')}</option>
          <option value="custom">{t('video2.avatarCustom')}</option>
        </select>

        {avatarType === 'preset' && (
          <select
            className="field"
            value={avatarPresetId}
            onChange={(event) => setAvatarPresetId(event.target.value)}
          >
            {AVATAR_PRESETS.map((preset) => (
              <option key={preset} value={preset}>
                {preset}
              </option>
            ))}
          </select>
        )}
        {avatarType === 'custom' && (
          <textarea
            className="composer-input"
            rows={2}
            value={customAvatarIdsInput}
            onChange={(event) => setCustomAvatarIdsInput(event.target.value)}
            placeholder={t('video2.customAvatarIds')}
          />
        )}

        <label className="field-label" htmlFor="video2-ad-approach">
          {t('video2.adApproach')}
        </label>
        <select
          id="video2-ad-approach"
          className="field"
          value={adApproach}
          onChange={(event) => setAdApproach(event.target.value as 'reference' | 'blocks')}
        >
          <option value="reference">{t('video2.adApproachReference')}</option>
          <option value="blocks">{t('video2.adApproachBlocks')}</option>
        </select>

        {adApproach === 'reference' ? (
          <input
            className="field"
            value={adReferenceId}
            onChange={(event) => setAdReferenceId(event.target.value)}
            placeholder={t('video2.adReferenceId')}
          />
        ) : (
          <>
            <input
              className="field"
              value={hookId}
              onChange={(event) => setHookId(event.target.value)}
              placeholder={t('video2.hookId')}
            />
            <input
              className="field"
              value={settingId}
              onChange={(event) => setSettingId(event.target.value)}
              placeholder={t('video2.settingId')}
            />
          </>
        )}

        <label className="field-label" htmlFor="video2-mode">
          {t('video2.mode')}
        </label>
        <select
          id="video2-mode"
          className="field"
          value={mode}
          onChange={(event) => setMode(event.target.value as (typeof VIDEO2_MODES)[number])}
        >
          {VIDEO2_MODES.map((modeValue) => (
            <option key={modeValue} value={modeValue}>
              {modeValue}
            </option>
          ))}
        </select>

        <label className="field-label" htmlFor="video2-ad-format">
          {t('video2.adFormat')}
        </label>
        <select
          id="video2-ad-format"
          className="field"
          value={adFormat}
          onChange={(event) => setAdFormat(event.target.value as (typeof AD_FORMATS)[number])}
        >
          {AD_FORMATS.map((format) => (
            <option key={format} value={format}>
              {format}
            </option>
          ))}
        </select>
        {adFormat === 'dtc-ads' && (
          <input
            className="field"
            value={formatId}
            onChange={(event) => setFormatId(event.target.value)}
            placeholder={t('video2.formatId')}
          />
        )}

        <div className="video-form-grid">
          <div>
            <label className="field-label" htmlFor="video2-duration">
              {t('video2.duration')}
            </label>
            <select
              id="video2-duration"
              className="field"
              value={duration}
              onChange={(event) => setDuration(event.target.value)}
            >
              <option value="10">10</option>
              <option value="15">15</option>
              <option value="20">20</option>
              <option value="30">30</option>
            </select>
          </div>
          <div>
            <label className="field-label" htmlFor="video2-resolution">
              {t('video2.resolution')}
            </label>
            <select
              id="video2-resolution"
              className="field"
              value={resolution}
              onChange={(event) => setResolution(event.target.value)}
            >
              <option value="480p">480p</option>
              <option value="720p">720p</option>
            </select>
          </div>
          <div>
            <label className="field-label" htmlFor="video2-aspect">
              {t('video2.aspectRatio')}
            </label>
            <select
              id="video2-aspect"
              className="field"
              value={aspectRatio}
              onChange={(event) => setAspectRatio(event.target.value)}
            >
              <option value="auto">auto</option>
              <option value="21:9">21:9</option>
              <option value="16:9">16:9</option>
              <option value="4:3">4:3</option>
              <option value="1:1">1:1</option>
              <option value="3:4">3:4</option>
              <option value="9:16">9:16</option>
            </select>
          </div>
        </div>

        <label className="field-checkbox">
          <input
            type="checkbox"
            checked={generateAudio}
            onChange={(event) => setGenerateAudio(event.target.checked)}
          />
          <span>{t('video2.generateAudio')}</span>
        </label>

        <button
          className="btn btn-primary"
          type="button"
          disabled={!canGenerate || generating}
          onClick={() => void handleGenerate()}
        >
          {t('composer.generate')}
        </button>
      </div>

      <div className="job-list">
        {loading && <p>{t('common.loading')}</p>}
        {!loading && sectionJobs.length === 0 && <p>{t('job.empty')}</p>}
        {sectionJobs.map((job: Job) => {
          const input = (job.input ?? {}) as Record<string, unknown>;
          return (
            <article key={job.id} className={`job-item job-${job.status}`}>
              <div className="job-header">
                <span className="job-model">{job.modelId}</span>
                <span className="job-status">
                  {t(`job.${job.status}` as Parameters<typeof t>[0])}
                </span>
              </div>
              <p className="job-prompt">{String(input.prompt ?? '')}</p>
              {job.resultUrl ? (
                <a
                  href={job.resultUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-ghost job-result"
                >
                  {t('job.result')}
                </a>
              ) : null}
              {job.error ? <p className="job-error">{job.error}</p> : null}
            </article>
          );
        })}
      </div>
    </section>
  );
}
